import classNames from 'classnames';
import { FocusScope, useKeyboard } from 'react-aria';

import Icon from '../../../app/shared/components/Icon/Icon';
import Button from '../Button/Button';
import css from './SidePanel.css';

/*
 * Shared by the trigger's aria-controls and the panel's id.
 * Safe only while at most one side panel is open at a time.
 * Replace this with a generated unique id if multiple side panels needs to be open simultaneously.
 */
const SIDE_PANEL_ID = 'side-panel';

interface SidePanelTriggerProps {
  children: React.ReactNode;
  isPressed: boolean;
  onClick: () => void;
  className?: string;
}

export const SidePanelTrigger = ({ children, className, isPressed, onClick }: SidePanelTriggerProps) => {
  return (
    <Button
      className={classNames(css.trigger, className)}
      aria-expanded={isPressed}
      aria-controls={isPressed ? SIDE_PANEL_ID : undefined}
      size="small"
      theme="primary"
      variant="outline"
      onClick={onClick}
    >
      {children}
    </Button>
  );
};

interface SidePanel extends Pick<React.ComponentProps<'aside'>, 'aria-label' | 'aria-labelledby' | 'className'> {
  children: React.ReactNode;
  closeLabel: string;
  onClose: () => void;
}

export const SidePanel = ({ children, className, closeLabel, onClose, ...props }: SidePanel) => {
  const { keyboardProps } = useKeyboard({
    onKeyDown: (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    },
  });

  return (
    <FocusScope restoreFocus autoFocus>
      {/*
       * The <aside> scrolls, the inner element lays the content out. Keeping them apart means the
       * panel's bounded height never shrinks the content: it grows to its natural size and scrolls.
       */}
      <aside id={SIDE_PANEL_ID} className={css.panel} tabIndex={-1} {...props} {...keyboardProps}>
        <div className={className}>
          <button type="button" className={css.closeButton} aria-label={closeLabel} onClick={onClose}>
            <Icon name="cross" className={css.icon} />
          </button>
          {children}
        </div>
      </aside>
    </FocusScope>
  );
};
